import type { AddressType, ApoOffice, ApoStateCode } from "../mailingAddress";

export type Program = "boys" | "girls";

export type Gender = "Male" | "Female" | "Other" | "Prefer not to say";

export type YesNo = "Yes" | "No";

export type ApplicationStatus =
  | "InProgress"
  | "Submitted"
  | "Submitted (Late)"
  | "Finalized"
  | "Incomplete"
  | "Reviewed"
  | "Accepted"
  | "Declined"
  | "Waitlisted";

// Two-milestone applicant status: an application is "Submitted" when the form is
// complete, then "Finalized" once the applicant confirms a sponsor Post/Unit in
// the post-submission step. Sponsor status is tracked separately from the admin
// ApplicationStatus (Reviewed/Accepted/etc.), so finalizing never resets review.
export type DraftStatus =
  | "InProgress"
  | "Submitted"
  | "Finalized"
  | "Incomplete"
  | "AmendmentInProgress";

export type DoseForm =
  | "tablet"
  | "capsule"
  | "liquid"
  | "inhaler"
  | "injection"
  | "topical"
  | "drops"
  | "other";

// Round 11 — expanded Health History grid (replaces the old Medical History
// yes/no grid AND the standalone asthma question; the grid's asthma row is now
// the single source of truth for the asthma flag). Row labels/extras live in
// healthConditions.ts (HEALTH_CONDITIONS).
export type HealthConditionKey =
  | "diabetes"
  | "hypertension"
  | "heartDisease"
  | "familyHeartHistory"
  | "strokeTia"
  | "asthma"
  | "lungDisease"
  | "copd"
  | "earEyesNoseSinus"
  | "musculoskeletal"
  | "headInjury"
  | "altitudeSickness"
  | "psychiatric"
  | "neurological"
  | "bloodDisorders"
  | "fainting"
  | "kidneyDisease"
  | "seizures"
  | "abdominal"
  | "thyroid"
  | "skin"
  | "sleepApnea"
  | "surgeries"
  | "otherConditions";

// One generic slot per extra-field kind; which slots a row actually uses (and
// their labels) is declared by its HealthConditionSpec. answer is required for
// Step 3 completeness; explanation (and a declared extraYesNo) are required on
// Yes; extraText/extraDate are optional.
export interface HealthConditionEntry {
  answer: YesNo | null;
  explanation: string;
  extraText: string;
  extraYesNo: YesNo | null;
  extraDate: string;
}

export type DiseaseKey = "chickenPox" | "germanMeasles" | "measles" | "mumps";

export type TimeOfDay = "morning" | "noon" | "evening" | "prn" | "other";

export interface UploadedFileRef {
  fieldKey: string;
  filename: string;
  contentType: string;
  sizeBytes: number;
  // Drive-relative path where the file is stored (or a "local:" stand-in before
  // the draft exists). Used by validation to detect "uploaded".
  storageKey: string;
  // Graph driveItem id of the stored file, when uploaded to SharePoint.
  storageItemId?: string;
  uploadedAt: string;
}

export interface Step1ProgramData {
  sessionYear: number;
  // Required attestation: the applicant must acknowledge the full-session /
  // secure-a-sponsor expectation before Step 1 counts as complete or the
  // application can be submitted. The sponsor itself is captured later, in the
  // post-submission sponsor step (see SponsorInfo).
  attestationAcknowledged: boolean;
}

export interface Step2BasicInfo {
  isUSCitizen: YesNo | null;
  isUSLegalResident: YesNo | null;
  studentEmail: string;
  parentEmail: string;
  legalFirstName: string;
  legalMiddleName: string;
  legalLastName: string;
  suffix: string;
  preferredName: string;
  dob: string;
  ageAtProgram: number | null;
  gender: Gender;
  // Round 22 (R-02) — which mailing-address shape the applicant is entering.
  // Drives which of the three field sets below is collected, validated and
  // rendered; only one set is ever populated. Absent on drafts saved before
  // Round 22, which normalizeAddressType() reads as "US" (see mailingAddress.ts).
  addressType: AddressType;
  // --- US path (unchanged from earlier rounds) ---
  homeStreet: string;
  homeCity: string;
  // County is captured explicitly for the outreach roster (Name, City, County,
  // School, Graduation Year). It is not derivable from the other address fields.
  // On the two non-US paths it is not user-entered at all — it is always OCONUS.
  homeCounty: string;
  homeState: string;
  homeZip: string;
  // --- APO/FPO/DPO path (military & diplomatic mail) ---
  // No city or country name is collected on purpose: including one routes the
  // mail into the destination country's postal system instead of the military
  // one. Addressing to "Any Service Member" is not permitted, so the recipient
  // name is a required named individual.
  apoRecipientName: string;
  apoUnitBox: string;
  apoOffice: ApoOffice | "";
  apoStateCode: ApoStateCode | "";
  apoZip: string;
  // --- Foreign path ---
  // Five free-text lines rather than structured street/city/province/postal
  // fields: formats vary too much between countries to validate cleanly.
  foreignAddressLine1: string;
  foreignAddressLine2: string;
  foreignAddressLine3: string;
  foreignAddressLine4: string;
  foreignAddressLine5: string;
  cellPhone: string;
  highSchoolName: string;
  graduationYear: number | null;
  eligibilityJunior: boolean;
  eligibilitySemesterRemaining: boolean;
  eligibilityNoOtherProgram: boolean;
  eligibilityFullSession: boolean;
  gpa: number | null;
  classRank: string;
  parent1Name: string;
  parent1Relationship: string;
  parent1Phone: string;
  parent1Email: string;
  parent2Name: string;
  parent2Relationship: string;
  parent2Phone: string;
  parent2Email: string;
  emergencyContactName: string;
  emergencyContactRelationship: string;
  emergencyContactPhone: string;
  tshirtSize: "XS" | "S" | "M" | "L" | "XL" | "XXL" | "XXXL" | "";
}

export interface MedicationRow {
  name: string;
  strength: string;
  doseQty: string;
  doseForm: DoseForm | "";
  timesOfDay: TimeOfDay[];
  timeOfDayOther: string;
}

// Round 12 — required single-select category on every allergy row.
export const ALLERGY_CATEGORIES = [
  "Food",
  "Medication",
  "Environmental",
  "Insect",
  "Other",
] as const;
export type AllergyCategory = (typeof ALLERGY_CATEGORIES)[number];

// Round 12 — self-carried emergency medications (inhaler / EpiPen / other).
export const SELF_CARRY_MED_TYPES = ["Inhaler", "Epinephrine auto-injector", "Other"] as const;
export type SelfCarryMedType = (typeof SELF_CARRY_MED_TYPES)[number];

export interface AllergyRow {
  allergy: string;
  reaction: string;
  anaphylaxisRisk: YesNo | null;
  bringingEpiPen: YesNo | null;
  // "" = unanswered (back-filled drafts); required before Step 3 is complete.
  category: AllergyCategory | "";
}

// Round 18 — Over-the-Counter (OTC) Medication Authorization. The list itself
// and the parent-facing wording live in shared/src/otcMedications.ts; these are
// just the stored shapes. blank/undefined per-item permission => treated as "no"
// (not authorized) — the safest administration default (see otcPermissionFor).
export type OtcPermission = "yes" | "no" | "call";

export interface OtcSelection {
  id: string; // stable item id, e.g. "acetaminophen"
  permission: OtcPermission;
}

// Round 19 — the OTC follow-up questions became required Yes/No selections with
// a required detail box on Yes, mirroring the Health History grid's
// answer/explanation shape (HealthConditionEntry). `answer` reuses the shared
// YesNo type so the same YesNoRadio selector drives both places; `detail` is the
// conditional explanation (required, non-empty, only when answer === "Yes").
export interface OtcFollowupAnswer {
  answer: YesNo | null; // must be non-null to complete Step 3 / submit
  detail: string; // required (non-empty) when answer === "Yes"
}

export interface OtcAuthorization {
  listVersion: string; // e.g. "2027.1" — from otcMedications.ts (OTC_LIST_VERSION)
  selections: OtcSelection[]; // one per item the parent explicitly answered
  // Round 20 — the only surviving follow-up question. priorOtcReaction /
  // doNotGive / otherNotes were removed (see otcMedications.ts OTC_QUESTIONS).
  // Stored drafts may still carry those three keys; they are simply no longer
  // read, exactly as Round 19 handled pre-Round-19 plain-string leftovers.
  medAllergies: OtcFollowupAnswer;
  acknowledged: boolean; // z.boolean(); must be true to submit
  acknowledgementSignature: string | null; // react-signature-canvas data URL (or typed name)
  // Set on the acknowledgement false->true edge and refreshed on amendment
  // re-attestation, following the Round 17 attestation pattern.
  acknowledgedAtUtc: string | null;
}

// Round 20 — Sunscreen & Insect Repellent Authorization. DELIBERATELY a separate
// object/section from OtcAuthorization: a parent can authorize one product
// without the other, and (per the program's medical supervisor) these are
// explicitly NOT medications — no dose/frequency/administration logging, no
// Physician Medication Authorization, and they never join the medications table.
// The brand strings and the empty-state/fold helpers live in
// shared/src/topicalsAuthorization.ts; these are just the stored shapes.
// A blank (null) permission reads as "No / not authorized" at export/PDF/tracker
// time — the same safest-default rule as the OTC per-item selections.
export interface TopicalItemPermissions {
  give: OtcPermission | null; // give/apply the product to the child
  assist: OtcPermission | null; // assist the child in applying it
}

export interface TopicalsAuthorization {
  listVersion: string; // e.g. "2027.1" — from topicalsAuthorization.ts (TOPICALS_VERSION)
  sunscreen: TopicalItemPermissions;
  insectRepellent: TopicalItemPermissions;
  acknowledged: boolean; // must be true to complete Step 3 / submit
  acknowledgementSignature: string | null; // react-signature-canvas data URL (or typed name)
  // Stamped on the acknowledgement false->true edge and cleared on amendment
  // re-attestation, mirroring OtcAuthorization.acknowledgedAtUtc.
  acknowledgedAtUtc: string | null;
}

export interface Step3HealthHistory {
  pcpName: string;
  pcpPhone: string;
  pcpAddress: string;
  healthConditions: Record<HealthConditionKey, HealthConditionEntry>;
  diseases: Record<DiseaseKey, YesNo | null>;
  accommodationRequest: YesNo | null;
  accommodationDescription: string;
  healthChangesSincePhysical: YesNo | null;
  healthChangesDescription: string;
  takesMedications: YesNo | null;
  medications: MedicationRow[];
  medicationAckSignature: string;
  medicationAckSignatureDate: string;
  // Round 12 — self-carry / emergency medications gate. On Yes the type
  // multi-select is required (+ free text when "Other" is selected).
  selfCarriesEmergencyMeds: YesNo | null;
  selfCarryMedTypes: SelfCarryMedType[];
  selfCarryMedOther: string;
  hasAllergies: YesNo | null;
  allergies: AllergyRow[];
  vaccinesUpToDate: YesNo | null;
  vaccinesComments: string;
  lastPhysicalDate: string;
  // Round 20 — parent sunscreen/insect-repellent authorization block. Sits
  // ALONGSIDE otcAuthorization (never inside it) and renders BEFORE it in the
  // form/PDF: the two consent types must never blur together.
  topicalsAuthorization: TopicalsAuthorization;
  // Round 18 — parent OTC medication authorization block (additive; coexists
  // with the medications table + Physician Medication Authorization above).
  otcAuthorization: OtcAuthorization;
}

export interface Step4Insurance {
  carrierName: string;
  policyId: string;
  groupNumber: string;
  policyholderName: string;
  policyholderRelationship: string;
  policyholderDob: string;
  claimsPhone: string;
}

export interface Step5Certifications {
  financialResponsibilityAck: boolean;
  liabilityWaiverAck: boolean;
  photoReleaseAck: boolean;
  photoReleaseSignature: string;
  codeOfConductAck: boolean;
  codeOfConductSignature: string;
  // Round 9 — new parent certification (on-screen only; no downloadable/uploadable form).
  medicalTreatmentAck: boolean;
  medicalTreatmentSignature: string;
  parentGuardianSignature: string;
  parentGuardianSignatureDate: string;
  applicantSignature: string;
  applicantSignatureDate: string;
  finalAttestation: boolean;
  // Round 17 — per-party signature attestations. Each must be acknowledged to
  // submit; the server stamps a UTC timestamp on the draft envelope when it sees
  // the acknowledgment flip true (see draft-service ServerMeta), and clears it on
  // amendment re-attestation. The booleans are client-owned (like the other
  // acks); the timestamps are server-owned and never trusted from the client.
  applicantCertificationAck: boolean;
  parentCertificationAck: boolean;
}

// Captured in the post-submission sponsor step (NOT part of the main form). The
// application submits with no sponsor; the applicant returns via their magic link
// to confirm a sponsor Post/Unit, which moves status Submitted -> Finalized.
export interface SponsorInfo {
  sponsorConfirmed: boolean;
  sponsorPostName: string;
  sponsorPostNumber: string;
  sponsorConfirmedDate: string;
}

export interface ApplicationData {
  step1: Step1ProgramData;
  step2: Step2BasicInfo;
  step3: Step3HealthHistory;
  step4: Step4Insurance;
  step5: Step5Certifications;
  sponsor: SponsorInfo;
  uploads: Record<string, UploadedFileRef>;
  meta: {
    currentStep: number;
    program: Program;
    submittedAt: string | null;
    lastAmendedAt: string | null;
    amendmentCount: number;
    referenceNumber: string | null;
    status: DraftStatus;
    completionPercent?: number;
    // True only when the submission-confirmation email actually sent — the
    // confirmation screen's wording must stay truthful (Round 10 Phase C).
    confirmationEmailSent?: boolean;
  };
}

export const UPLOAD_FIELDS = {
  proofOfResidency: "proof-of-residency",
  schoolCertification: "school-certification",
  physicianStatement: "physician-statement",
  asthmaActionPlan: "asthma-action-plan",
  medicationAuthorization: "medication-authorization",
  medicalRecord: "medical-record",
  insuranceCardFront: "insurance-card-front",
  insuranceCardBack: "insurance-card-back",
  financialResponsibility: "financial-responsibility",
  liabilityWaiver: "liability-waiver",
} as const;

export type UploadFieldKey = (typeof UPLOAD_FIELDS)[keyof typeof UPLOAD_FIELDS];

export const TEMPLATE_FORMS = [
  "school-certification",
  "physician-statement",
  "asthma-action-plan",
  "medication-authorization",
  "financial-responsibility",
  "liability-waiver",
] as const;

export type TemplateFormName = (typeof TEMPLATE_FORMS)[number];
